import { ProviderRegistry } from '@config/providers/provider.registry';
import { DEFAULT_CHAT_CONFIG } from '@constants/chat';
import { normalizeChats } from '@utils/chatNavigation';
import { StoreApi, create } from 'zustand';
import { persist, PersistStorage } from 'zustand/middleware';
import { devtools } from 'zustand/middleware';

import { AuthSlice, createAuthSlice } from './auth-slice';
import { ChatSlice, createChatSlice } from './chat-slice';
import { ConfigSlice, createConfigSlice } from './config-slice';
import { InputSlice, createInputSlice } from './input-slice';
import { PromptSlice, createPromptSlice } from './prompt-slice';
import { RequestSlice, createRequestSlice } from './request-slice';

export type StoreState = ChatSlice &
  InputSlice &
  AuthSlice &
  ConfigSlice &
  PromptSlice &
  RequestSlice;

export type StoreSlice<T> = (
  set: StoreApi<StoreState>['setState'],
  get: StoreApi<StoreState>['getState'],
  store: StoreApi<StoreState>
) => T;

// Custom storage with error handling
const createCustomStorage = (): PersistStorage<Partial<StoreState>> => {
  let isHandlingError = false;  // Add error handling flag
  
  return {
    getItem: (name) => {
      try {
        const item = localStorage.getItem(name);
        return item ? JSON.parse(item) : null;
      } catch (err: unknown) {
        const error = err as Error;
        console.warn('Error reading from localStorage:', error);
        return null;
      }
    },
    setItem: (name, value) => {
      if (isHandlingError) return;  // Prevent recursive error handling
      
      try {
        localStorage.setItem(name, JSON.stringify(value));
      } catch (err: unknown) {
        isHandlingError = true;
        const error = err as Error;
        if (error instanceof Error && error.name === 'QuotaExceededError') {
          useStore.getState().setError(
            'Storage limit reached. Please delete some chats to continue saving new messages.'
          );
        } else {
          useStore.getState().setError(
            'Failed to save to localStorage. ' + error.message
          );
        }
        isHandlingError = false;
      }
    },
    removeItem: (name) => {
      try {
        localStorage.removeItem(name);
      } catch (err: unknown) {
        const error = err as Error;
        console.warn('Error removing from localStorage:', error);
      }
    }
  };
};

// Ensure chats is always an array
export const useStore = create<StoreState>()(
  devtools(
    persist(
      (...args) => ({
        ...createAuthSlice(...args),
        ...createChatSlice(...args), // Make sure this initializes chats: []
        ...createConfigSlice(...args),
        ...createInputSlice(...args),
        ...createPromptSlice(...args),
        ...createRequestSlice(...args),
      }),
      {
        name: 'free-chat-gpt',
        version: 3, // Increment version for stable message ids and chat view state
        storage: createCustomStorage(),
        partialize: (state) => ({
          chats: state.chats,
          currentChatIndex: state.currentChatIndex,
          apiKeys: state.apiKeys,
          apiEndpoints: state.apiEndpoints,
          theme: state.theme,
          autoTitle: state.autoTitle,
          prompts: state.prompts,
          defaultChatConfig: state.defaultChatConfig,
          defaultSystemMessage: state.defaultSystemMessage,
          hideMenuOptions: state.hideMenuOptions,
          firstVisit: state.firstVisit,
          hideSideMenu: state.hideSideMenu,
          folders: state.folders,
          enterToSubmit: state.enterToSubmit,
          layoutWidth: state.layoutWidth,
        }),
        onRehydrateStorage: () => (state) => {
          if (!state) {
            return;
          }

          if (!Array.isArray(state.chats)) {
            console.warn('🔧 Fixing corrupted chats state:', state.chats);
            state.chats = [];
            return;
          }

          state.chats = normalizeChats(state.chats);
        },
        migrate: (persistedState: any, version: number) => {
          let migratedState = persistedState;

          if (version === 1) {
            // Migrate old provider configuration to new format
            const chats = persistedState.chats?.map((chat: any) => ({
              ...chat,
              config: {
                provider: chat.config?.provider || 'anthropic',
                modelConfig: {
                  ...chat.config?.modelConfig,
                  model: ProviderRegistry.getProvider('anthropic').capabilities.defaultModel ?? DEFAULT_CHAT_CONFIG.modelConfig.model,
                },
              },
            }));

            migratedState = {
              ...persistedState,
              chats,
              defaultChatConfig: DEFAULT_CHAT_CONFIG,
            };
          }

          return {
            ...migratedState,
            chats: normalizeChats(migratedState?.chats),
          };
        },
      }
    )
  ) // ← ADD THIS CLOSING PARENTHESIS
);

export default useStore;
